import { useMemo } from 'react';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useGetSalesGroup } from 'src/hooks/api/useGetSalesGroup';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { EventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBody } from 'src/modals/Modal';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { SalesGroupDetails } from 'src/WebApiController';

import {
  BuyerSection,
  EarningsSection,
  PaymentSection,
  SaleSection,
  SummarySection,
  TicketsSection,
} from './SalesGroupModal.components';
import { SalesGroupModalFooter } from './SalesGroupModalFooter';

/**
 * Read-only Sales Group view modal (POS-8632), aligned to Figma. Renders a plain
 * "View Detail" modal header (no event-connected header / purple Summary card)
 * and — in Figma order — SALE, SUMMARY, Tickets, Earnings, Payment,
 * and Buyer Delivery. The member-sales list is no longer rendered.
 *
 * The group is the single source of truth: SALE / SUMMARY read straight from the
 * SalesGroupDetails payload (plus a representative member sale for merchant /
 * sale-date / created-by), so there is no clicked-sale / active-entity context.
 *
 * Section chrome mirrors SaleDetailsV2's SaleDetailsModalBody: tables use the
 * borderless AllocatedListing markup (Thead showBottomBorder={false} + tableRow
 * on every header/body row), and detail blocks use the responsive
 * SectionContentGridFormat where SaleDetailsV2 does.
 *
 * All financial values come straight from the backend (SalesGroupDetails) —
 * nothing financial is recomputed here.
 *
 * Read-only by design: per-sale edits live on SaleDetailsV2.
 */
export const SalesGroupModal = ({ salesGroupId }: { salesGroupId: string }) => {
  const {
    data: group,
    isLoading: salesGroupIsLoading,
    isError,
  } = useGetSalesGroup(salesGroupId);

  const isLoading = salesGroupIsLoading;
  // Render the "View Detail" header ONCE, then switch inner content between the
  // spinner / not-found / loaded body so the header is never duplicated across
  // the three states.
  return (
    <>
      <CancellableFormHeader>
        <EventEntityHeader title={<Content id={ContentId.ViewDetail} />} />
      </CancellableFormHeader>
      {isLoading ? (
        <ModalBody>
          <PosSpinner />
        </ModalBody>
      ) : isError || !group ? (
        <ModalBody>
          <EmptySectionContent.Root>
            <Content id={ContentId.SalesGroupNotFound} />
          </EmptySectionContent.Root>
        </ModalBody>
      ) : (
        <SalesGroupModalContent group={group} salesGroupId={salesGroupId} />
      )}
    </>
  );
};

/**
 * Loaded-state body + footer for the Sales Group view modal. Kept in this file
 * (co-located with the container) while the low-level section components live in
 * ./SalesGroupModal.components. Renders the sections in Figma order and the
 * group-level footer; the footer only exists in the loaded state.
 */
const SalesGroupModalContent = ({
  group,
  salesGroupId,
}: {
  group: SalesGroupDetails;
  salesGroupId: string;
}) => {
  // Derive the member-sales array once — it is passed to the footer, so memoizing
  // avoids handing it a fresh [] on every render.
  const sales = useMemo(() => group.sales ?? [], [group.sales]);
  const ticketRows = useMemo(() => group.ticketRows ?? [], [group.ticketRows]);

  return (
    <>
      <ModalBody>
        <ModalBodyDataContainer>
          <SaleSection group={group} />
          <SummarySection group={group} />
          <TicketsSection rows={ticketRows} />
          <EarningsSection group={group} />
          <PaymentSection group={group} />
          <BuyerSection group={group} />
        </ModalBodyDataContainer>
      </ModalBody>
      <SalesGroupModalFooter
        salesGroupId={salesGroupId}
        sales={sales}
        group={group}
      />
    </>
  );
};
